import { api } from './api';
import { z } from 'zod';

/** Which notifications a connected user receives. */
export const NOTIFICATION_KEYS = [
  'imports',
  'financial_alerts',
  'extraordinary_expenses',
  'access_changes',
  'security_logins',
] as const;

export type NotificationKey = (typeof NOTIFICATION_KEYS)[number];

export const NOTIFICATION_LABELS: Record<NotificationKey, string> = {
  imports: 'Импорты',
  financial_alerts: 'Финансовые предупреждения',
  extraordinary_expenses: 'Внеплановые расходы',
  access_changes: 'Изменения доступа',
  security_logins: 'Новые входы',
};

/** `configured` is about the installation, `connected` about this user. */
export const telegramStatusSchema = z.object({
  configured: z.boolean(),
  connected: z.boolean(),
  telegram_username: z.string().nullable().optional().default(null),
  connected_at: z.string().nullable().optional().default(null),
  notification_preferences: z.record(z.boolean()).default({}),
});

/** Returned once, at creation. The server keeps only the token's hash.
 *
 * Minted by the owner for a named user in Настройки → Пользователи и роли —
 * see `usersService.telegramLink`. Nobody creates one for themselves.
 */
export const telegramLinkSchema = z.object({
  link: z.string(),
  expires_at: z.string(),
});

export type TelegramStatus = z.infer<typeof telegramStatusSchema>;
export type TelegramLink = z.infer<typeof telegramLinkSchema>;
export type TelegramPreferences = Record<NotificationKey, boolean>;

export const telegramService = {
  status: (signal?: AbortSignal): Promise<TelegramStatus> =>
    api.get('/integrations/telegram/status', telegramStatusSchema, signal),

  disconnect: (): Promise<TelegramStatus> =>
    api.post('/integrations/telegram/disconnect', telegramStatusSchema),

  setPreferences: (preferences: TelegramPreferences): Promise<TelegramStatus> =>
    api.patch('/integrations/telegram/preferences', telegramStatusSchema, preferences),
};
